import React, { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import type { AnomaliaElemento } from '@/hooks/useAnomalias';

interface Props {
  anomalias: AnomaliaElemento[];
  onResolver?: (anomaliaId: string) => Promise<void> | void;
  size?: 'sm' | 'md';
}

const severidadColor: Record<string, string> = {
  alta: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/40 dark:text-red-200',
  media: 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/40 dark:text-orange-200',
  baja: 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/40 dark:text-yellow-200',
};

export const AnomaliasIndicator: React.FC<Props> = ({ anomalias, onResolver, size = 'md' }) => {
  const activas = useMemo(() => anomalias.filter((a) => !a.resuelta), [anomalias]);
  const [resolverId, setResolverId] = useState<string | null>(null);

  if (activas.length === 0) return null;

  const handleResolver = async (id: string) => {
    if (!onResolver) return;
    setResolverId(id);
    try {
      await onResolver(id);
    } finally {
      setResolverId(null);
    }
  };

  const iconSize = size === 'sm' ? 'h-4 w-4' : 'h-5 w-5';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-950/30 transition-colors"
          title={`${activas.length} anomalía(s) activa(s)`}
          onClick={(e) => e.stopPropagation()}
        >
          <AlertTriangle className={iconSize} />
          <span className="text-xs font-semibold">{activas.length}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-96 p-0 max-h-[60vh] overflow-y-auto"
        align="start"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-3 border-b">
          <h4 className="text-sm font-semibold flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-orange-500" />
            {activas.length} anomalía{activas.length === 1 ? '' : 's'} activa{activas.length === 1 ? '' : 's'}
          </h4>
        </div>
        <div className="divide-y">
          {activas.map((a) => {
            const info = a.codigo_info;
            const sev = info?.severidad || 'media';
            return (
              <div key={a.id} className="p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-[10px] font-mono">
                        {a.codigo_anomalia}
                      </Badge>
                      <Badge variant="outline" className={`text-[10px] capitalize ${severidadColor[sev] || ''}`}>
                        {sev}
                      </Badge>
                    </div>
                    <p className="text-sm font-medium mt-1 leading-snug">
                      {info?.descripcion || 'Sin descripción del código'}
                    </p>
                  </div>
                </div>
                {a.descripcion && (
                  <p className="text-xs text-muted-foreground italic">"{a.descripcion}"</p>
                )}
                {info?.solucion_sugerida && (
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">Solución sugerida:</span> {info.solucion_sugerida}
                  </p>
                )}
                {info?.referencia_normativa && (
                  <p className="text-[10px] text-muted-foreground">
                    Ref. {info.referencia_normativa}
                  </p>
                )}
                <div className="flex items-center justify-between pt-1">
                  <span className="text-[10px] text-muted-foreground">
                    {format(new Date(a.fecha_deteccion), "d MMM yyyy, HH:mm", { locale: es })}
                  </span>
                  {onResolver && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs gap-1"
                      onClick={() => handleResolver(a.id)}
                      disabled={resolverId === a.id}
                    >
                      {resolverId === a.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <CheckCircle2 className="h-3 w-3" />
                      )}
                      Resolver
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
